import { TEMPLATE_CATEGORY_OPTIONS } from '../templateCategory';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Input, Container, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { useReactToPrint } from 'react-to-print';
import { ActionCreators } from 'redux-undo';
import Swal from 'sweetalert2';
import { UploadCloud, RotateCcw, RotateCw, ZoomIn, ZoomOut, ArrowLeft, Eye, Plus, Printer, AlignLeft } from 'react-feather';
import Breadcrumbs from '../../../../CommonElements/Breadcrumbs/Breadcrumbs';
import { useAppDispatch, useAppSelector } from '../../../../ReaduxToolkit/Hooks';
import { hydrate, setDocumentMeta, removeElements, updateElements, duplicateElements, createBlankDocument, addPage, applyLayoutChanges, autoFormatDocument } from './store/formDocumentSlice';
import { computeAutoFormatChanges } from './layout/autoFormatLayout';
import { setSelection, setZoom, setClient, setTemplateCategory, setPublishStatus, setImportStatus, setActiveTool } from './store/formDesignerUiSlice';
import { getLayout, publishForm, uploadTemplate } from './store/formDesignerThunks';
import { serializeToDocxBlob } from './export/docxSerializer';
import { buildOutputSchema, fetchClientSelectedFieldNames, fetchMasterFields, findFieldInstruction, SectionFields } from './masterFields';
import { findPageIdForSelection } from './pageUtils';
import { collectTemplateFields, collectVariableFieldKeys, FILLABLE_TYPES } from './fieldUsage';
import Toolbox from './Toolbox/Toolbox';
import TemplateFieldsPanel from './Toolbox/TemplateFieldsPanel';
import DynamicVariablePalette from './Toolbox/DynamicVariablePalette';
import PageCanvas from './Canvas/PageCanvas';
import PropertiesPanel from './PropertiesPanel/PropertiesPanel';
import PrintPreview from './export/PrintPreview';
import api from '../../../../api';
import { AesDecrypt, AesEncrypt, keyPromise } from '../../../../Service/crypto';
import './formDesigner.css';

interface ClientOption {
  label: string;
  value: string;
}

export default function FormDesigner() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const doc = useAppSelector((s) => s.FormDocument.present);
  const canUndo = useAppSelector((s) => s.FormDocument.past.length > 0);
  const canRedo = useAppSelector((s) => s.FormDocument.future.length > 0);
  const zoom = useAppSelector((s) => s.FormDesignerUi.zoom);
  const selectedIds = useAppSelector((s) => s.FormDesignerUi.selectedIds);
  const activeTool = useAppSelector((s) => s.FormDesignerUi.activeTool);
  const publishStatus = useAppSelector((s) => s.FormDesignerUi.publishStatus);
  const importStatus = useAppSelector((s) => s.FormDesignerUi.importStatus);
  const clientId = useAppSelector((s) => s.FormDesignerUi.clientId);
  const clientName = useAppSelector((s) => s.FormDesignerUi.clientName);
  const templateCategory = useAppSelector((s) => s.FormDesignerUi.templateCategory);

  const isEditMode = Boolean(id);
  const [templateId, setTemplateId] = useState<string | null>(id || null);
  // Type already saved on the template: read-only while editing
  const [categoryLocked, setCategoryLocked] = useState(false);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [masterFields, setMasterFieldsState] = useState<SectionFields>({});
  const [outputSchema, setOutputSchema] = useState<Record<string, string>>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const usertype = localStorage.UserType;
  const printRef = useRef<HTMLDivElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: doc.name || 'Form',
  });

  useEffect(() => {
    fetchMasterFields(clientId).then(setMasterFieldsState).catch(() => { });
    setOutputSchema({});
    fetchClientSelectedFieldNames(clientId)
      .then((names) => setOutputSchema(buildOutputSchema(names)))
      .catch(() => { });
  }, [clientId]);

  useEffect(() => {
    const init = async () => {
      if (id) {
        try {
          const result = await getLayout(id);
          if ('layoutSchema' in result && result.layoutSchema) {
            dispatch(hydrate(result.layoutSchema));
            dispatch(setClient({ clientId: result.clientId || null, clientName: result.clientName || null }));
            dispatch(setTemplateCategory(result.templateCategory || ''));
            setCategoryLocked(Boolean(result.templateCategory));
            setTemplateId(result.templateId);
          } else {
            dispatch(hydrate(createBlankDocument()));
          }
        } catch (err) {
          // Backend unreachable or template no longer exists — fall back to a blank
          // document rather than leaving the designer stuck or showing a raw error.
          console.error('Error loading form layout:', err);
          dispatch(hydrate(createBlankDocument()));
        }
      } else {
        dispatch(hydrate(createBlankDocument()));
        if (usertype === 'Client') {
          dispatch(setClient({ clientId: localStorage.ClientId, clientName: localStorage.ClientName }));
        }
      }
      dispatch(ActionCreators.clearHistory());
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (usertype === 'MOS') {
      api
        .fetch('api/clientmanagement/get_client', { method: 'GET' })
        .then((r) => r.json())
        .then(async (r) => {
          const k = await keyPromise;
          const decrypted = await AesDecrypt(k, r);
          if (Array.isArray(decrypted)) {
            setClients(decrypted.map((c: any) => ({ label: c.CompanyName, value: c._id || c.id })));
          }
        })
        .catch(() => { });
    } else if (usertype === 'Vendor') {
      keyPromise
        .then((k) => AesEncrypt(k, { id: localStorage.VendorId }))
        .then((EncParams) => api.fetch('api/TemplateCreation/vendorbasedclientOptions', { method: 'POST', body: JSON.stringify({ EncParams }) }))
        .then((r) => r.json())
        .then(async (r) => {
          const k = await keyPromise;
          const decrypted = await AesDecrypt(k, r);
          if (Array.isArray(decrypted)) {
            setClients(decrypted.map((c: any) => ({ label: c.CompanyName, value: c._id || c.id })));
          }
        })
        .catch(() => { });
    }
  }, [usertype]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        dispatch(e.shiftKey ? ActionCreators.redo() : ActionCreators.undo());
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        dispatch(ActionCreators.redo());
        return;
      }
      if (e.key === 'Escape' && activeTool) {
        dispatch(setActiveTool(null));
        return;
      }

      // Duplicate/Delete act on whichever page the current selection actually lives on —
      // not necessarily the first page, once a document has more than one.
      const targetPageId = findPageIdForSelection(doc, selectedIds);
      if (!targetPageId || selectedIds.length === 0) return;

      // Delete on a bound input only removes its field; the input itself stays on the template
      const targetPage = doc.pages.find((p) => p.id === targetPageId);
      const selectedEls = targetPage?.elements.filter((el) => selectedIds.includes(el.id)) || [];
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selectedEls.length > 0 &&
        selectedEls.every((el) => FILLABLE_TYPES.has(el.type) && el.bindings?.dynamicField)
      ) {
        e.preventDefault();
        dispatch(updateElements({
          pageId: targetPageId,
          changes: selectedEls.map((el) => ({ id: el.id, changes: { bindings: { ...el.bindings, dynamicField: undefined, sectionKey: undefined } } })),
        }));
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        dispatch(duplicateElements({ pageId: targetPageId, ids: selectedIds }));
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        dispatch(removeElements({ pageId: targetPageId, ids: selectedIds }));
        dispatch(setSelection([]));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dispatch, doc, selectedIds, activeTool]);

  const handleUploadTemplateClick = () => {
    const hasContent = doc.pages.some((page) => page.elements.length > 0);
    if (hasContent) {
      Swal.fire({
        title: 'Insurance Verification Platform',
        text: 'Uploading a template will replace everything currently on the canvas. Continue?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Continue',
      }).then((result) => {
        if (result.isConfirmed) uploadInputRef.current?.click();
      });
    } else {
      uploadInputRef.current?.click();
    }
  };

  const handleUploadTemplateFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    // Show the loader the moment a file is picked, before validation and upload.
    dispatch(setImportStatus('importing'));

    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    const typeOk = !file.type || file.type === 'application/pdf';
    let signatureOk = false;
    try {
      const head = new Uint8Array(await file.slice(0, 1024).arrayBuffer());
      signatureOk = new TextDecoder('latin1').decode(head).trimStart().startsWith('%PDF-');
    } catch {
      signatureOk = false;
    }
    if (ext !== '.pdf' || !typeOk || !signatureOk) {
      dispatch(setImportStatus('idle'));
      Swal.fire({ title: 'Insurance Verification Platform', text: 'Only PDF files are allowed. Please upload a valid .pdf file.', icon: 'warning' });
      return;
    }

    try {
      const result = await uploadTemplate(file);
      dispatch(hydrate(result.layoutSchema));
      dispatch(ActionCreators.clearHistory());
      dispatch(setImportStatus('idle'));
      if (result.warnings && result.warnings.length > 0) {
        Swal.fire({
          title: 'Template imported with notes',
          html: `<ul style="text-align:left">${result.warnings.map((w) => `<li>${w}</li>`).join('')}</ul>`,
          icon: 'info',
        });
      }
    } catch (err: any) {
      console.error(err);
      dispatch(setImportStatus('error'));
      const serverMsg = err?.response?.data?.error;
      Swal.fire({ title: 'Insurance Verification Platform', text: serverMsg || 'Failed to parse the uploaded file.', icon: 'error' });
    }
  };

  const handleParseContent = () => {
    dispatch(autoFormatDocument());
    const totalEditable = doc.pages.reduce(
      (acc, p) =>
        acc +
        p.elements.filter(
          (e) =>
            !e.locked &&
            (e.type === 'textbox' ||
              e.type === 'checkbox' ||
              e.type === 'radio' ||
              e.type === 'textarea' ||
              e.type === 'date')
        ).length,
      0
    );
    Swal.fire({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2500,
      icon: 'success',
      title: `Parsed content: ${totalEditable} editable fields ready.`,
    });
  };

  const handleSubmit = async () => {
    if (!doc.name || !doc.name.trim()) {
      Swal.fire({ title: 'Insurance Verification Platform', text: 'Please enter a Template Name before submitting.', icon: 'warning' });
      return;
    }
    if ((usertype === 'MOS' || usertype === 'Vendor') && !clientId) {
      Swal.fire({ title: 'Insurance Verification Platform', text: 'Please select a client before submitting.', icon: 'warning' });
      return;
    }
    if (!templateCategory) {
      Swal.fire({ title: 'Insurance Verification Platform', text: 'Please select a Template Type before submitting.', icon: 'warning' });
      return;
    }
    dispatch(setPublishStatus('saving'));
    try {
      const docxBlob = await serializeToDocxBlob(doc);
      // { "<Manage Label section>": [{ field_name, instruction }] } for every field placed on the template.
      // Re-read the Manage Label list so instructions edited since the designer opened are the ones saved.
      const freshFields = await fetchMasterFields(clientId).catch(() => ({} as SectionFields));
      const instructionSource = Object.keys(freshFields).length > 0 ? freshFields : masterFields;
      // Keyed by the label printed on the template; each entry carries the Manage Label field and its instruction.
      const fieldWithInstruction: Record<string, { Template_field_name: string; field_name: string; instruction: string }[]> = {};
      const seen = new Set<string>();
      const add = (label: string, fieldKey: string) => {
        const found = findFieldInstruction(instructionSource, fieldKey);
        const key = label || found?.sectionKey || 'General';
        const dedupe = `${key}||${fieldKey}`;
        if (seen.has(dedupe)) return;
        seen.add(dedupe);
        if (!fieldWithInstruction[key]) fieldWithInstruction[key] = [];
        fieldWithInstruction[key].push({
          Template_field_name: label,
          field_name: fieldKey,
          instruction: found?.instruction || '',
        });
      };
      collectTemplateFields(doc).forEach((f) => {
        const fieldKey = f.element.bindings?.dynamicField;
        if (fieldKey) add(f.hint, fieldKey);
      });
      // fields placed some other way (inline variables in text) have no input row to take a label from
      collectVariableFieldKeys(doc).forEach((fieldKey) => {
        if (![...seen].some((s) => s.endsWith(`||${fieldKey}`))) add('', fieldKey);
      });

      // Submit may beat the client-change fetch, so build it here if it isn't ready yet
      const schema = Object.keys(outputSchema).length > 0
        ? outputSchema
        : buildOutputSchema(await fetchClientSelectedFieldNames(clientId));

      const result = await publishForm({
        templateId,
        layoutSchema: doc,
        name: doc.name,
        clientId,
        clientName,
        templateCategory,
        docxBlob,
        fieldWithInstruction,
        outputSchema: schema,
      });
      setTemplateId(result.templateId);
      dispatch(setPublishStatus('saved'));
      Swal.fire({
        title: 'Insurance Verification Platform',
        html: '<span style="color: green;">Form submitted successfully!</span>',
        icon: 'success',
        allowOutsideClick: false,
      }).then(() => navigate('/templateCreation', { state: { tab: 'form' } }));
    } catch (err) {
      console.error(err);
      dispatch(setPublishStatus('error'));
      Swal.fire({ title: 'Insurance Verification Platform', text: 'Failed to submit form.', icon: 'error' });
    }
  };

  return (
    <>
      <Breadcrumbs mainTitle={isEditMode ? (doc.name || 'Form Designer') : 'Form Designer'} title="Form Designer" />
      <Container fluid>
        <div className="fd-shell">
          <div className="fd-topbar">
            <Button size="sm" color="light" outline onClick={() => navigate('/templateCreation', { state: { tab: 'form' } })} title="Back to templates">
              <ArrowLeft size={14} />
            </Button>
            <Input
              bsSize="sm"
              style={{ width: 220 }}
              value={doc.name}
              onChange={(e) => dispatch(setDocumentMeta({ name: e.target.value }))}
              placeholder="Enter Template Name"
              disabled={isEditMode}
              required
            />
            {(usertype === 'MOS' || usertype === 'Vendor') && (
              <Input
                type="select"
                bsSize="sm"
                style={{ width: 200 }}
                value={clientId || ''}
                onChange={(e) => {
                  const opt = clients.find((c) => c.value === e.target.value);
                  dispatch(setClient({ clientId: opt?.value || null, clientName: opt?.label || null }));
                }}
                disabled={isEditMode}
                required
              >
                <option value="">Select client...</option>
                {clients.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Input>
            )}

            <Input
              type="select"
              bsSize="sm"
              style={{ width: 150 }}
              value={templateCategory}
              onChange={(e) => dispatch(setTemplateCategory(e.target.value))}
              disabled={isEditMode && categoryLocked}
              required
            >
              <option value="">Template Type...</option>
              {TEMPLATE_CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Input>

            <Button size="sm" color="light" outline disabled={!canUndo} onClick={() => dispatch(ActionCreators.undo())} title="Undo (Ctrl+Z)">
              <RotateCcw size={14} />
            </Button>
            <Button size="sm" color="light" outline disabled={!canRedo} onClick={() => dispatch(ActionCreators.redo())} title="Redo (Ctrl+Shift+Z)">
              <RotateCw size={14} />
            </Button>

            <Button size="sm" color="light" outline onClick={() => dispatch(setZoom(zoom - 0.1))} title="Zoom out">
              <ZoomOut size={14} />
            </Button>
            <span style={{ minWidth: 42, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
            <Button size="sm" color="light" outline onClick={() => dispatch(setZoom(zoom + 0.1))} title="Zoom in">
              <ZoomIn size={14} />
            </Button>
            <Button size="sm" color="light" outline onClick={() => dispatch(addPage())} title="Add page">
              <Plus size={14} /> Page
            </Button>

            {!isEditMode && (
              <>
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  style={{ display: 'none' }}
                  onChange={handleUploadTemplateFile}
                />
                <Button
                  size="sm"
                  color="light"
                  outline
                  onClick={handleUploadTemplateClick}
                  disabled={importStatus === 'importing'}
                  title="Upload a PDF to auto-generate a template"
                >
                  {importStatus === 'importing' ? 'Uploading...' : <><UploadCloud size={14} /> Upload Template</>}
                </Button>
              </>
            )}

            <Button
              size="sm"
              color="light"
              outline
              onClick={handleParseContent}
              disabled={importStatus === 'importing'}
              title="Reflow the current content into clean, non-overlapping rows"
            >
              <AlignLeft size={14} /> Parse Content
            </Button>

            <div className="fd-topbar-spacer" />

            <Button size="sm" color="light" outline onClick={() => setPreviewOpen(true)} title="Preview">
              <Eye size={14} /> Preview
            </Button>
            <Button size="sm" color="primary" onClick={handleSubmit} disabled={publishStatus === 'saving'}>
              {publishStatus === 'saving' ? 'Submitting...' : 'Submit'}
            </Button>
          </div>

          <div className="fd-body">
            <div className="fd-toolbox-panel">
              <Toolbox />
              <DynamicVariablePalette />
            </div>
            <TemplateFieldsPanel masterFields={masterFields} />
            <div
              className="fd-canvas-area"
              onMouseDown={(e) => {
                // Clicking the grey area around the pages deselects, which hides the properties panel
                if (e.target === e.currentTarget && !activeTool) dispatch(setSelection([]));
              }}
            >
              {doc.pages.map((page) => (
                <PageCanvas key={page.id} pageId={page.id} />
              ))}
            </div>
            {selectedIds.length > 0 && (
              <div className="fd-properties-container">
                <PropertiesPanel />
              </div>
            )}
          </div>
        </div>
      </Container>

      <Modal isOpen={previewOpen} toggle={() => setPreviewOpen(false)} size="lg" className="modal-dialog-centered modal-dialog-scrollable">
        <ModalHeader toggle={() => setPreviewOpen(false)}>
          Preview: {doc.name}
          <Button size="sm" color="primary" className="ms-3" onClick={() => handlePrint()}>
            <Printer size={14} /> Print / Save as PDF
          </Button>
        </ModalHeader>
        <ModalBody style={{ background: '#e9ecef' }}>
          <PrintPreview ref={printRef} document={doc} />
        </ModalBody>
      </Modal>

      {publishStatus === 'saving' && (
        <div>
          <div className="loading-overlay">
            <div id="page">
              <div id="container">
                <div id="ring"></div>
                <div id="ring"></div>
                <div id="ring"></div>
                <div id="ring"></div>
                <div id="h3" style={{ color: 'white' }}>Submitting...</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {importStatus === 'importing' && (
        <div className="loading-overlay" role="status" aria-live="polite">
          <div className="fd-upload-loader">
            <div className="fd-upload-spinner" />
            <div className="fd-upload-loader-title">Uploading &amp; analyzing PDF...</div>
            <div className="fd-upload-loader-sub">Please wait, this can take a few seconds. Do not close or refresh the page.</div>
          </div>
        </div>
      )}
    </>
  );
}
